import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, Download, Search, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { trNorm } from '@/lib/arama';

const typeLabels = {
  sale: 'Satis',
  refund: 'Iade',
  complimentary: 'Ikram',
  no_charge: 'Odenmez'
};

const typeColors = {
  sale: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  refund: 'bg-[#7a3535]/10 text-[#7a3535]',
  complimentary: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  no_charge: 'bg-[#5a5a7a]/10 text-[#5a5a7a]'
};

export default function SalesList() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [saleTypeFilter, setSaleTypeFilter] = useState('all');
  
  // Pagination & sort
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [sortBy, setSortBy] = useState('sale_date');
  const [sortDir, setSortDir] = useState('desc');

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/pos/sales?start_date=${startDate}&end_date=${endDate}`;
      const res = await api.get(url);
      let salesData = res.data.sales || [];
      
      // Apply filters client-side
      if (saleTypeFilter && saleTypeFilter !== 'all') {
        salesData = salesData.filter(s => s.sale_type === saleTypeFilter);
      }
      if (search) {
        const q = trNorm(search);
        salesData = salesData.filter(s =>
          trNorm(s.product_name).includes(q) ||
          trNorm(s.product_code).includes(q)
        );
      }
      
      // Sort
      salesData.sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
      
      setSales(salesData);
    } catch (err) {
      toast.error('Ürün satış raporu yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, saleTypeFilter, search, sortBy, sortDir]);

  useEffect(() => { fetchSales(); }, [fetchSales]);
  useEffect(() => { setPage(1); }, [saleTypeFilter, search, startDate, endDate]);

  const toggleSort = (col) => {
    if (sortBy === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortBy(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown size={10} className="ml-0.5 text-[#d0c4b0]" />;
    return sortDir === 'asc' ? <ArrowUp size={10} className="ml-0.5 text-[#1a1916]" /> : <ArrowDown size={10} className="ml-0.5 text-[#1a1916]" />;
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      let url = `/pos/sales/export?start_date=${startDate}&end_date=${endDate}`;
      if (saleTypeFilter && saleTypeFilter !== 'all') {
        url += `&sale_type=${saleTypeFilter}`;
      }
      const res = await api.get(url);
      const data = res.data.data || [];
      if (data.length === 0) {
        toast.error('Disa aktarilacak veri yok');
        return;
      }
      
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(';'),
        ...data.map(row => headers.map(h => {
          let val = row[h];
          if (typeof val === 'number') val = val.toString().replace('.', ',');
          return `"${val || ''}"`;
        }).join(';'))
      ].join('\n');
      
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = res.data.filename || `pos_sales_${startDate}_${endDate}.csv`;
      link.click();
      
      toast.success('Excel dosyasi indirildi');
    } catch (err) {
      toast.error('Disa aktarma hatasi');
    } finally {
      setExporting(false);
    }
  };

  // Calculate KPIs from filtered data
  const kpis = {
    total_qty: sales.reduce((sum, s) => sum + (s.quantity || 0), 0),
    total_amount: sales.filter(s => s.sale_type === 'sale').reduce((sum, s) => sum + (s.total_amount || 0), 0),
    total_cost: sales.reduce((sum, s) => sum + (s.total_cost || 0), 0),
    sale: sales.filter(s => s.sale_type === 'sale').reduce((sum, s) => sum + (s.quantity || 0), 0),
    refund: sales.filter(s => s.sale_type === 'refund').reduce((sum, s) => sum + (s.quantity || 0), 0),
    complimentary: sales.filter(s => s.sale_type === 'complimentary').reduce((sum, s) => sum + (s.quantity || 0), 0),
    no_charge: sales.filter(s => s.sale_type === 'no_charge').reduce((sum, s) => sum + (s.quantity || 0), 0),
  };
  kpis.profit = kpis.total_amount - kpis.total_cost;

  // Pagination
  const totalItems = sales.length;
  const totalPages = Math.ceil(totalItems / perPage);
  const paginatedSales = sales.slice((page - 1) * perPage, page * perPage);

  const thClass = "text-[#1a1916] font-bold text-[9px] uppercase tracking-wider cursor-pointer select-none hover:bg-[#d0c4b0]/30 transition-colors py-1.5";

  return (
    <div data-testid="sales-list-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1916] tracking-tight flex items-center gap-2">
            <TrendingUp size={24} /> Ürün Satış Raporu
          </h1>
          <p className="text-xs text-[#8a7e70]">
            POS üzerinden satılan ürünler — satış <strong>belgesi</strong> için
            Satış Faturaları kullanılır
          </p>
        </div>
        <Button 
          onClick={exportToExcel}
          disabled={exporting || sales.length === 0}
          variant="outline"
          size="sm"
          className="h-7 text-xs border-[#3d6e4e] text-[#3d6e4e] hover:bg-[#3d6e4e]/10"
        >
          <Download size={12} className="mr-1" />
          {exporting ? 'Indiriliyor...' : 'Excel'}
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-3 text-center">
            <p className="text-[9px] font-bold uppercase text-[#8a7e70]">Toplam Adet</p>
            <p className="text-lg font-bold text-[#1a1916]">{kpis.total_qty?.toFixed(0) || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-3 text-center">
            <p className="text-[9px] font-bold uppercase text-[#8a7e70]">Toplam Tutar</p>
            <p className="text-lg font-bold text-[#3d6e4e]">{(kpis.total_amount || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} TL</p>
          </CardContent>
        </Card>
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-3 text-center">
            <p className="text-[9px] font-bold uppercase text-[#8a7e70]">Toplam Maliyet</p>
            <p className="text-lg font-bold text-[#7a3535]">{(kpis.total_cost || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} TL</p>
          </CardContent>
        </Card>
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-3 text-center">
            <p className="text-[9px] font-bold uppercase text-[#8a7e70]">Kar / Zarar</p>
            <p className={`text-lg font-bold ${(kpis.profit || 0) >= 0 ? 'text-[#3d6e4e]' : 'text-[#7a3535]'}`}>
              {(kpis.profit || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} TL
            </p>
          </CardContent>
        </Card>
        <Card className="bg-[#3d6e4e]/10 border-[#3d6e4e]/30">
          <CardContent className="p-3 text-center">
            <p className="text-[9px] font-bold uppercase text-[#3d6e4e]">Satis</p>
            <p className="text-lg font-bold text-[#3d6e4e]">{kpis.sale?.toFixed(0) || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#7a3535]/10 border-[#7a3535]/30">
          <CardContent className="p-3 text-center">
            <p className="text-[9px] font-bold uppercase text-[#7a3535]">Iade</p>
            <p className="text-lg font-bold text-[#7a3535]">{kpis.refund?.toFixed(0) || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#7a6a2e]/10 border-[#7a6a2e]/30">
          <CardContent className="p-3 text-center">
            <p className="text-[9px] font-bold uppercase text-[#7a6a2e]">Ikram</p>
            <p className="text-lg font-bold text-[#7a6a2e]">{kpis.complimentary?.toFixed(0) || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative flex-1 min-w-[150px] max-w-xs">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
          <Input 
            placeholder="Urun ara..." 
            value={search} 
            onChange={(e) => setSearch(e.target.value)}
            className="pl-7 h-7 text-xs bg-white border-[#d0c4b0]" 
          />
        </div>
        <Select value={saleTypeFilter} onValueChange={setSaleTypeFilter}>
          <SelectTrigger className="w-28 h-7 text-xs bg-white border-[#d0c4b0]">
            <SelectValue placeholder="Islem Tipi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Tipler</SelectItem>
            {Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input 
          type="date" 
          value={startDate} 
          onChange={(e) => setStartDate(e.target.value)} 
          className="w-28 h-7 text-xs bg-white border-[#d0c4b0]" 
        />
        <Input 
          type="date" 
          value={endDate} 
          onChange={(e) => setEndDate(e.target.value)} 
          className="w-28 h-7 text-xs bg-white border-[#d0c4b0]" 
        />
      </div>

      {/* Table */}
      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className={thClass} onClick={() => toggleSort('sale_date')}>
                <div className="flex items-center">Tarih<SortIcon col="sale_date" /></div>
              </TableHead>
              <TableHead className={thClass} onClick={() => toggleSort('pos_ticket_id')}>
                <div className="flex items-center">Adisyon No<SortIcon col="pos_ticket_id" /></div>
              </TableHead>
              <TableHead className={thClass} onClick={() => toggleSort('sale_type')}>
                <div className="flex items-center">Tip<SortIcon col="sale_type" /></div>
              </TableHead>
              <TableHead className={thClass}>Urun Kodu</TableHead>
              <TableHead className={thClass} onClick={() => toggleSort('product_name')}>
                <div className="flex items-center">Urun Adi<SortIcon col="product_name" /></div>
              </TableHead>
              <TableHead className={`${thClass} text-right`} onClick={() => toggleSort('quantity')}>
                <div className="flex items-center justify-end">Miktar<SortIcon col="quantity" /></div>
              </TableHead>
              <TableHead className={thClass}>Birim</TableHead>
              <TableHead className={`${thClass} text-right`} onClick={() => toggleSort('unit_price')}>
                <div className="flex items-center justify-end">B.Fiyat<SortIcon col="unit_price" /></div>
              </TableHead>
              <TableHead className={`${thClass} text-right`} onClick={() => toggleSort('total_amount')}>
                <div className="flex items-center justify-end">Tutar<SortIcon col="total_amount" /></div>
              </TableHead>
              <TableHead className={`${thClass} text-right`} onClick={() => toggleSort('unit_cost')}>
                <div className="flex items-center justify-end">B.Maliyet<SortIcon col="unit_cost" /></div>
              </TableHead>
              <TableHead className={`${thClass} text-right`} onClick={() => toggleSort('total_cost')}>
                <div className="flex items-center justify-end">T.Maliyet<SortIcon col="total_cost" /></div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedSales.map((s, idx) => (
              <TableRow key={`${s.sale_date}-${s.product_code}-${s.sale_type}-${idx}`} className="border-[#d0c4b0]">
                <TableCell className="text-[10px] text-[#3F3E39] whitespace-nowrap py-1">
                  {s.sale_date}
                </TableCell>
                <TableCell className="font-mono text-[9px] text-[#1a1916] py-1">
                  {s.pos_ticket_id || '-'}
                </TableCell>
                <TableCell className="py-1">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${typeColors[s.sale_type] || 'bg-[#e8dfd0] text-[#3F3E39]'}`}>
                    {typeLabels[s.sale_type] || s.sale_type}
                  </span>
                </TableCell>
                <TableCell className="font-mono text-[9px] text-[#8a7e70] py-1">{s.product_code}</TableCell>
                <TableCell className="text-[10px] font-medium text-[#1a1916] py-1">{s.product_name}</TableCell>
                <TableCell className="text-right text-[10px] font-semibold text-[#1a1916] py-1">
                  {s.quantity?.toFixed(2)}
                </TableCell>
                <TableCell className="text-[9px] text-[#8a7e70] py-1">{s.unit}</TableCell>
                <TableCell className="text-right font-mono text-[10px] text-[#3F3E39] py-1">
                  {s.unit_price?.toFixed(2)}
                </TableCell>
                <TableCell className={`text-right font-mono text-[10px] font-semibold py-1 ${s.sale_type === 'sale' ? 'text-[#3d6e4e]' : 'text-[#8a7e70]'}`}>
                  {(s.total_amount || 0).toFixed(2)}
                </TableCell>
                <TableCell className="text-right font-mono text-[10px] text-[#3F3E39] py-1">
                  {s.unit_cost?.toFixed(2)}
                </TableCell>
                <TableCell className="text-right font-mono text-[10px] text-[#7a3535] py-1">
                  {(s.total_cost || 0).toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
            {paginatedSales.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-6 text-[10px] text-[#8a7e70]">
                  {loading ? 'Yukleniyor...' : 'Satis bulunamadi'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2 text-[10px] text-[#8a7e70]">
          <span>Sayfa basina:</span>
          <Select value={String(perPage)} onValueChange={(v) => { setPerPage(Number(v)); setPage(1); }}>
            <SelectTrigger className="w-16 h-6 text-[10px] bg-white border-[#d0c4b0]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
              <SelectItem value="500">500</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#8a7e70]">
            {totalItems > 0 ? `${(page - 1) * perPage + 1}-${Math.min(page * perPage, totalItems)} / ${totalItems}` : '0'}
          </span>
          <Button variant="outline" size="icon" className="h-6 w-6 border-[#d0c4b0]"
            disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft size={12} />
          </Button>
          <Button variant="outline" size="icon" className="h-6 w-6 border-[#d0c4b0]"
            disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight size={12} />
          </Button>
        </div>
      </div>
    </div>
  );
}
